import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {ProvisionAgentBalanceDto} from '../../../../../model/dto/entity/provision-agent-balance.dto';
import {cpReserveUnderfunded} from '../../../../../service/domain/cp-reserve-underfunded';

/**
 * navItem "Konten/Salden" of the ProvisionAgent OBC (04-frontend.md §7), widget alias
 * `cp.agent.balance`.
 *
 * Shows the FOUR balance values of R-03 side by side — provision account and reserve account each
 * real (ACTIVE only) and simulated (ACTIVE + CALCULATED) — plus `lastCalculatedDate`.
 *
 * UNDERFUNDING HINT (U-03, the payout brake): a negative REAL reserve balance is shown prominently
 * as an error message. This is not a cosmetic hint — a negative reserve account blocks the agent's
 * payout, and the caseworker must see exactly this information BEFORE starting a payout run. A
 * negative SIMULATED balance while the real balance is still positive is shown as a warning (a
 * preview of the effect of already calculated but not yet activated bookings) and is deliberately
 * NOT called an underfunding — the run brakes on the real balance alone.
 *
 * <h2>The brake predicate is not written here — and ideally would not be written in the frontend</h2>
 * This component is the SECOND surface of the same OBC that states the brake; the first is
 * {@code CpProvisionAgentAccountComponent}, which READS
 * {@code ProvisionAccountStatementResponseDto.reserveUnderfunded} — the very verdict
 * {@code ProvisionPayoutService} decides payouts on. Two surfaces of one OBC stating one rule in two
 * ways is how they come to disagree, and a `reserveBalance < 0` open-coded in this file was exactly
 * that second way.
 *
 * <p>It cannot simply read the flag: this component's data path is the persisted row
 * {@code cp.ProvisionAgentBalance} through the GENERIC CRUD service (see below), and that row
 * carries the four balance figures and no brake flag. Calling the statement endpoint from a compact
 * side panel to learn one boolean would fetch a page of bookings, an opening-balance aggregate and
 * two counts — six statements by that service's own pinned budget — and discard all of it.
 *
 * <p>So the rule is stated in the frontend exactly ONCE, in
 * {@link cpReserveUnderfunded}, which documents itself as a MIRROR of the backend predicate and as
 * an interim measure. THE REAL FIX IS A BACKEND ONE: publish the same verdict as a derived field on
 * the {@code cp.ProvisionAgentBalance} read path, at which point that file is deleted and this
 * component reads the flag like the account component does.
 *
 * Data path: the balance row is 1:1 with the agent but has its own id. It is therefore
 * loaded via the generic CRUD service of alias `cp.ProvisionAgentBalance` (registry entry in
 * cp.entity-provider.ts) with a filter on `provisionAgent`; its id then feeds the
 * read-only data widget `cp.agent.balance`. The widget itself is a "data" widget
 * (`createWidgetEntityData`) and therefore READ-ONLY — derived balances must not be editable.
 *
 * Refresh, TWO sources — both are necessary because the balances arise in two different ways:
 *   1. `MvsObjectService.objectChangeEvent` for `cp.ProvisionAgentTransaction` (bm original
 *      `bm-customer-account-transaction.component.ts#subscribeToObjectChanges`) — the manual
 *      booking and the transfer write synchronously and report via this channel.
 *   2. `JbSpringBatchIndicatorService.status$` (04-frontend.md §4) — the four balance values are
 *      recalculated by the balance job `cp_provision_balance`. The job reports NO objectChangeEvent
 *      (it runs in the backend), so without this subscription exactly the numbers the job just
 *      overwrote would remain stale. Reloaded on the running -> not-running transition.
 */
@Component({
    selector: 'cp-provision-agent-balance',
    templateUrl: './cp-provision-agent-balance.component.html',
    styleUrls: ['./cp-provision-agent-balance.component.scss'],
    standalone: false
})
export class CpProvisionAgentBalanceComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionAgentId: number;

    /** The loaded balance row; null as long as not loaded or none exists yet. */
    balanceDto: ProvisionAgentBalanceDto;

    /** Read-only data widget on cp.ProvisionAgentBalance (alias cp.agent.balance). */
    balanceWidget: WidgetData;

    /**
     * THE PAYOUT BRAKE (U-03) — via {@link cpReserveUnderfunded}, the module's one frontend
     * statement of the rule, never open-coded here. See the class TSDoc for why this surface still
     * derives at all instead of reading a delivered flag.
     */
    reserveUnderfunded: boolean = false;

    /**
     * FORECAST, NOT THE BRAKE: only the SIMULATED reserve balance is negative, i.e. bookings that
     * are calculated but not yet activated would take the reserve account into the red.
     *
     * A plain sign test on a delivered figure, and deliberately NOT routed through
     * {@link cpReserveUnderfunded}: that function is the mirror of the payout run's guard, and the
     * run brakes on the REAL balance alone. Feeding a simulated figure into it would make this
     * warning look like the same verdict under a different colour.
     */
    reserveSimulatedUnderfunded: boolean = false;

    /** Is a cp job currently running? Feeds the interim-state hint of the tiles. */
    jobRunning: boolean;

    private objectChangeSubscription: Subscription;
    private statusSubscription: Subscription;
    private jobWasRunning: boolean;

    constructor(
        protected coreService: MvsCoreService,
        protected mvsObjectService: MvsObjectService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.subscribeToObjectChanges();
        this.subscribeToJobCompletion();
        this.refreshComponent();
    }

    /**
     * Reloads on every change to the module's account bookings — a booking changes the
     * derived balances, and the caseworker must not see a stale balance after a manual
     * booking.
     */
    private subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'cp.ProvisionAgentTransaction') {
                this.refreshComponent();
            }
        });
    }

    /**
     * Subscription to the job indicator; reload on the running -> not-running transition (04 §4).
     *
     * The indicator is neither built nor activated here — that is done by the shell CpDashboardPage
     * (`jobNamePattern: 'cp%'`). If the OBC runs outside that shell, `status$` simply never
     * delivers a transition; the component then behaves as before.
     */
    private subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.refreshComponent();
            }
        });
    }

    refreshComponent(): void {

        if (!this.provisionAgentId) {
            this.balanceDto = null;
            this.balanceWidget = null;
            this.reserveUnderfunded = false;
            this.reserveSimulatedUnderfunded = false;
            this.initialized = true;
            return;
        }

        this.busy = true;

        const filterCriteria = [
            FilterCriteria.create('provisionAgent', FilterCriteria.cOperatorEqual, this.provisionAgentId)
        ];

        const objectRequestList = ObjectRequestList.createBasic(false, filterCriteria, null);

        this.coreService.getCrudService('cp.ProvisionAgentBalance')
            .list(objectRequestList)
            .subscribe({
                next: (dtoList) => {
                    const entries = dtoList?.entries as ProvisionAgentBalanceDto[];
                    this.balanceDto = entries && entries.length ? entries[0] : null;
                    this.evaluateCoverage();
                    this.buildBalanceWidget();
                    this.busy = false;
                    this.initialized = true;
                },
                error: () => {
                    this.balanceDto = null;
                    this.balanceWidget = null;
                    this.busy = false;
                    this.initialized = true;
                }
            });
    }

    /**
     * Sets the two reserve flags from the loaded balance row.
     *
     * The BRAKE is not decided here — {@link cpReserveUnderfunded} decides it, so the module holds
     * the rule in one frontend place instead of one per surface. A missing (null) balance is NOT
     * negative there either: as long as the balance job has never run, no false alarm may be raised.
     *
     * The FORECAST is a local sign test on the simulated figure and stays local on purpose (see the
     * field TSDoc). It is suppressed while the brake is on, so the two notices never both shout:
     * the brake is the more severe statement and wins the precedence.
     */
    private evaluateCoverage(): void {
        const reserveSimulated = this.balanceDto?.reserveSimulatedBalance;

        this.reserveUnderfunded = cpReserveUnderfunded(this.balanceDto?.reserveBalance);
        this.reserveSimulatedUnderfunded =
            !this.reserveUnderfunded && reserveSimulated != null && reserveSimulated < 0;
    }

    /**
     * Builds the read-only data widget on the balance row (alias cp.agent.balance).
     */
    private buildBalanceWidget(): void {

        if (!this.balanceDto || !this.balanceDto.id) {
            this.balanceWidget = null;
            return;
        }

        /**
         * AI: Data widget with the complete derived balance row of the agent.
         *     Source: cp.ProvisionAgentBalance (1:1 with the agent), read-only.
         *     Complements the tiles above with all remaining fields of the entity.
         */
        this.balanceWidget = WidgetFactory.createWidgetEntityData(
            'cp.agent.balance',
            'Konten und Salden',
            'cp.ProvisionAgentBalance',
            this.balanceDto.id
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionAgentId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.objectChangeSubscription?.unsubscribe();
        this.statusSubscription?.unsubscribe();
    }

}
